import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Asset } from '../../models/Asset';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { loadModel } from '../../api/threed';
import { assignEuler, assignV3 } from '../../utils/spatial';

export function setupAssetPreview(
  container: HTMLElement,
  asset: Asset,
  modelBlob: Blob | null,
  setModelTree?: (modelScene: any) => void
) {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
  scene.background = new THREE.Color(0x212121);
  camera.up.set(0, 0, 1);
  camera.position.set(8, 0, 2);
  camera.lookAt(0, 0, 0);
  while (container.firstChild) {
    container.removeChild(container.firstChild as ChildNode);
  }
  const renderer = new THREE.WebGLRenderer();
  renderer.setSize(container.clientWidth, container.clientHeight);
  container.appendChild(renderer.domElement);

  const grid = new THREE.GridHelper(50, 50);
  grid.rotation.x = Math.PI / 2;
  scene.add(grid);

  const axesHelper = new THREE.AxesHelper(5);
  scene.add(axesHelper);

  const ambientLight = new THREE.AmbientLight(0xffffff, 3);
  scene.add(ambientLight);
  const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
  directionalLight.position.set(0, 0, 10);
  scene.add(directionalLight);

  const directionalLight2 = new THREE.DirectionalLight(0xffffff, 3);
  directionalLight2.position.set(0, 0, -10);
  scene.add(directionalLight2);

  const directionalLight3 = new THREE.DirectionalLight(0xffffff, 3);
  directionalLight3.position.set(10, 0, 0);
  scene.add(directionalLight3);

  const light = new THREE.PointLight(0xffffff, 1, 100);
  light.position.set(0, 0, 10);
  scene.add(light);

  let model: THREE.Object3D | null = null;
  let animationId: number;

  if (modelBlob) {
    const loader = new GLTFLoader();
    const objectUrl = URL.createObjectURL(modelBlob);
    loadModel(loader, objectUrl).then((gltf) => {
      model = gltf.scene;
      model.userData.gltf = gltf;
      model.position.set(...(asset.position as [number, number, number]));
      model.scale.set(asset.scale, asset.scale, asset.scale);
      (model as any).asset = asset;
      scene.add(model);
      if (setModelTree) {
        setModelTree(model);
      }
      URL.revokeObjectURL(objectUrl);
    }).catch((error) => {
      console.error("Error loading model from blob:", error);
    });
  }
  //  else if (asset.modelData) {
  //   const loader = new GLTFLoader();
  //   loader.parse(
  //     asset.modelData,
  //     '',
  //     (gltf) => {
  //       model = gltf.scene;
  //       model.position.set(asset.position[0], asset.position[1], asset.position[2]);
  //       model.scale.set(asset.scale, asset.scale, asset.scale);
  //       scene.add(model);
  //       console.log("New model added from modelData");
  //     },
  //     (err) => console.error('GLTF parse error:', err)
  //   );
  // }

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.25;
  controls.enableZoom = true;
  controls.enablePan = true;
  controls.screenSpacePanning = true;
  controls.target.set(asset.position[0], asset.position[1], asset.position[2]);
  controls.update();

  function updateModel() {
    if (model) {
      const attachedAsset = (model as any).asset;
      if (!attachedAsset) {
        return;
      }
      // console.log("Updating model from attached asset: ", attachedAsset);
      assignV3(model.position, attachedAsset.position);
      assignEuler(model.rotation, attachedAsset.rotation);
      assignV3(model.scale, attachedAsset.scale);
    }
  }

  function animate() {
    animationId = requestAnimationFrame(animate);
    updateModel();
    if (model && model.userData.mixer) {
      model.userData.mixer.update(0.01); // Update the animation mixer
    }
    controls.update();
    renderer.render(scene, camera);
  }
  animate();

  const onResize = () => {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  };
  window.addEventListener('resize', onResize);

  return {
    cleanup: () => {
      if (model) {
        console.log("removing the model");
        scene.remove(model);
        model.traverse((child) => {
          if ((child as THREE.Mesh).geometry) {
            (child as THREE.Mesh).geometry.dispose();
          }
          if ((child as THREE.Mesh).material) {
            const material = (child as THREE.Mesh).material;
            if (Array.isArray(material)) {
              material.forEach((mat) => mat.dispose());
            } else {
              material.dispose();
            }
          }
        });
        model = null;
        console.log("model removed");
      }

      cancelAnimationFrame(animationId);
      console.log("animation cancelled");

      renderer.dispose();
      console.log("renderer disposed");
      window.removeEventListener('resize', onResize);
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      console.log("renderer removed from DOM");
    }
  };

}
